import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "duds.cart.v1";

const loadCart = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.warn("[cart] failed to parse stored cart, resetting:", e);
    return [];
  }
};

const saveCart = (items) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    // localStorage can fail in private mode / quota; cart still works in-memory
    console.warn("[cart] failed to persist cart to localStorage:", e);
  }
};

const captureUtm = () => {
  try {
    const sp = new URLSearchParams(window.location.search);
    const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
    const out = {};
    let found = false;
    for (const k of keys) {
      const v = sp.get(k);
      if (v) {
        out[k.replace("utm_", "")] = v;
        found = true;
      }
    }
    if (found) {
      const existing = JSON.parse(localStorage.getItem("duds.utm") || "{}");
      localStorage.setItem("duds.utm", JSON.stringify({ ...existing, ...out }));
    }
    return JSON.parse(localStorage.getItem("duds.utm") || "{}");
  } catch (e) {
    console.warn("[utm] capture failed:", e);
    return {};
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => loadCart());
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    saveCart(items);
  }, [items]);

  useEffect(() => {
    captureUtm();
  }, []);

  const addItem = useCallback((product, size, quantity = 1, color = null) => {
    setItems((cur) => {
      const idx = cur.findIndex(
        (i) => i.product_id === product.id && i.size === size && (i.color || null) === (color || null),
      );
      if (idx >= 0) {
        const next = [...cur];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      // Use colour-specific cover image when colours are defined
      let image = product.images?.[0];
      if (color && product.colors) {
        const c = product.colors.find((x) => x.name === color);
        if (c?.images?.length) image = c.images[0];
      }
      return [
        ...cur,
        {
          product_id: product.id,
          name: product.name,
          size,
          color,
          quantity,
          price_usd: product.price_usd,
          image,
        },
      ];
    });
    setIsOpen(true);
  }, []);

  const updateQty = useCallback((product_id, size, quantity, color = null) => {
    setItems((cur) =>
      cur
        .map((i) =>
          i.product_id === product_id && i.size === size && (i.color || null) === (color || null)
            ? { ...i, quantity: Math.max(0, quantity) }
            : i,
        )
        .filter((i) => i.quantity > 0),
    );
  }, []);

  const removeItem = useCallback((product_id, size, color = null) => {
    setItems((cur) =>
      cur.filter((i) => !(i.product_id === product_id && i.size === size && (i.color || null) === (color || null))),
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const subtotal = useMemo(
    () => items.reduce((sum, i) => sum + i.price_usd * i.quantity, 0),
    [items],
  );
  const count = useMemo(
    () => items.reduce((sum, i) => sum + i.quantity, 0),
    [items],
  );

  const value = {
    items,
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen((v) => !v),
    addItem,
    updateQty,
    removeItem,
    clear,
    subtotal,
    count,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};

export const getStoredUtm = () => {
  try {
    return JSON.parse(localStorage.getItem("duds.utm") || "{}");
  } catch (e) {
    console.warn("[utm] read failed:", e);
    return {};
  }
};
